import { decodeReplay, encodeReplay, type Replay } from '../replay/ReplayFormat';
import { base64ToBytes, bytesToBase64 } from './base64';
import { isValidRunResult } from './LocalStorageScoreRepository';
import type { RunResult } from './ScoreRepository';

export interface PersonalRun {
  id: string;
  result: RunResult;
  replayId?: string;
  replay?: string;
}

export const PERSONAL_RUNS_KEY = 'laniaamu.runs';

/** Metadata and compressed demos for the ten fastest attempts in each category. */
export class PersonalRuns {
  private memory: PersonalRun[] = [];
  private volatile = false;

  top(category: string, legacyBest: RunResult | null = null): PersonalRun[] {
    const rows = this.read().filter((row) => row.result.category === category);
    if (legacyBest && !rows.some((row) => row.result.recordedAt === legacyBest.recordedAt &&
      row.result.timeMs === legacyBest.timeMs)) {
      rows.push({ id: 'legacy', result: legacyBest });
    }
    return rows.sort((a, b) => a.result.timeMs - b.result.timeMs).slice(0, 10);
  }

  save(result: RunResult, replay: Replay | null): string {
    const id = `local_${crypto.randomUUID()}`;
    let encoded: string | undefined;
    try { encoded = replay ? bytesToBase64(encodeReplay(replay)) : undefined; } catch { /* Keep the time. */ }
    const rows = [...this.read(), { id, result, replay: encoded }];
    const counts = new Map<string, number>();
    this.write(rows.sort((a, b) => a.result.timeMs - b.result.timeMs).filter((row) => {
      const count = (counts.get(row.result.category) ?? 0) + 1;
      counts.set(row.result.category, count);
      return count <= 10;
    }));
    return id;
  }

  share(id: string, replayId: string): void {
    this.write(this.read().map((row) => row.id === id ? { ...row, replayId } : row));
  }

  replay(id: string): Replay | null {
    const row = this.read().find((row) => row.id === id || row.replayId === id);
    try { return row?.replay ? decodeReplay(base64ToBytes(row.replay)) : null; } catch { return null; }
  }

  private read(): PersonalRun[] {
    if (this.volatile) return this.memory;
    try {
      const raw: unknown = JSON.parse(localStorage.getItem(PERSONAL_RUNS_KEY) ?? '[]');
      if (!Array.isArray(raw)) return this.memory;
      return raw.filter((row): row is PersonalRun => row && typeof row.id === 'string' &&
        isValidRunResult(row.result) && typeof row.result.category === 'string' &&
        (row.replayId === undefined || typeof row.replayId === 'string') &&
        (row.replay === undefined || typeof row.replay === 'string'));
    } catch { return this.memory; }
  }

  private write(rows: PersonalRun[]): void {
    this.memory = rows;
    try { localStorage.setItem(PERSONAL_RUNS_KEY, JSON.stringify(rows)); this.volatile = false; } catch {
      this.volatile = true;
      // Keep times and shareable capabilities even when local demos fill the quota.
      try {
        localStorage.setItem(PERSONAL_RUNS_KEY, JSON.stringify(rows.map((row) => ({ ...row, replay: undefined }))));
      } catch { /* Storage disabled: the session still has its results. */ }
    }
  }
}
